import useHttpProvider from "@/common/hooks/use-http";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { SiteSettings } from "@/common/services/site-settings";
import * as SiteSettingsService from "@/common/services/site-settings";

const STORAGE_KEY = "reelrewards_site_settings_v1";

const DEFAULT_SETTINGS: SiteSettings = {
	storeEnabled: true,
	cartEnabled: true,
	subscriptionCouponCodesEnabled: false,
	merchCouponCodesEnabled: true,
};

type SiteSettingsContextValue = {
	isLoading: boolean;
	settings: SiteSettings;
	refresh: () => Promise<void>;
};

const SiteSettingsContext = createContext<SiteSettingsContextValue | null>(null);

function readCachedSettings(): SiteSettings | null {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<SiteSettings>;
		if (
			typeof parsed?.storeEnabled !== "boolean" ||
			typeof parsed?.cartEnabled !== "boolean" ||
			typeof parsed?.subscriptionCouponCodesEnabled !== "boolean" ||
			typeof parsed?.merchCouponCodesEnabled !== "boolean"
		) {
			return null;
		}
		return parsed as SiteSettings;
	} catch {
		return null;
	}
}

function writeCachedSettings(next: SiteSettings) {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
	} catch {
		// ignore
	}
}

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
	const { http } = useHttpProvider();

	const [settings, setSettings] = useState<SiteSettings>(
		() => readCachedSettings() ?? DEFAULT_SETTINGS
	);
	const [isLoading, setIsLoading] = useState(true);

	const refresh = useCallback(async () => {
		const next = await SiteSettingsService.getSiteSettings(http);
		setSettings((prev) => {
			const merged: SiteSettings = {
				storeEnabled: typeof next.storeEnabled === "boolean" ? next.storeEnabled : prev.storeEnabled,
				cartEnabled: typeof next.cartEnabled === "boolean" ? next.cartEnabled : prev.cartEnabled,
				subscriptionCouponCodesEnabled:
					typeof next.subscriptionCouponCodesEnabled === "boolean"
						? next.subscriptionCouponCodesEnabled
						: prev.subscriptionCouponCodesEnabled,
				merchCouponCodesEnabled:
					typeof next.merchCouponCodesEnabled === "boolean"
						? next.merchCouponCodesEnabled
						: prev.merchCouponCodesEnabled,
			};
			writeCachedSettings(merged);
			return merged;
		});
	}, [http]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setIsLoading(true);
			try {
				await refresh();
			} catch {
				// Keep cached/default settings if the request fails.
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		void load();

		return () => {
			cancelled = true;
		};
	}, [refresh]);

	const value = useMemo(
		() => ({
			isLoading,
			settings,
			refresh,
		}),
		[isLoading, settings, refresh]
	);

	return (
		<SiteSettingsContext.Provider value={value}>
			{children}
		</SiteSettingsContext.Provider>
	);
}

export function useSiteSettings() {
	const ctx = useContext(SiteSettingsContext);
	if (!ctx) {
		throw new Error("useSiteSettings must be used within SiteSettingsProvider");
	}
	return ctx;
}
